import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown,
  IconLock,
  IconLockOpen,
  IconPlus,
  IconStackPush,
} from '@tabler/icons-react';
import {
  useCloseTitleRun,
  useCreateSupplement,
  useReopenTitleRun,
  useTitleRun,
} from '../../queries/title-runs';
import { useTract } from '../../queries/tracts';
import { useEntriesForRun } from '../../queries/runsheet-entries';
import { useProjectStore } from '../../project/project-store';
import { useAuthStore } from '../../auth/auth-store';
import { EntryGrid } from './EntryGrid';
import { EntryFormDrawer } from './EntryFormDrawer';
import { CommentsPanel } from './CommentsPanel';

export function RunsheetEditor() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const runQ = useTitleRun(id);
  const tractQ = useTract(runQ.data?.tract_id ?? null);
  const entriesQ = useEntriesForRun(id);
  const role = useProjectStore((s) => s.projectRole);
  const user = useAuthStore((s) => s.user);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';
  const canReopen = role === 'owner' || user?.system_role === 'superadmin';
  const closeMut = useCloseTitleRun();
  const reopenMut = useReopenTitleRun();
  const supplementMut = useCreateSupplement(id);

  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [drawerEntryId, setDrawerEntryId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (runQ.isLoading) {
    return (
      <Center h="60vh">
        <Loader />
      </Center>
    );
  }

  if (!runQ.data) {
    return (
      <Container>
        <Text>Title run not found.</Text>
      </Container>
    );
  }

  const run = runQ.data;
  const tract = tractQ.data;
  const entries = entriesQ.data ?? [];
  const isClosed = run.closed_at !== null;
  const isOriginal = run.kind === 'original';
  const canEdit = canWrite && !isClosed;

  function openSupplementModal() {
    let nextTitle = `${run.title} — Supplement`;
    modals.openConfirmModal({
      title: 'Create supplemental run',
      children: (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            New instruments will go on this supplemental. The original stays read-only.
          </Text>
          <TextInput
            label="Supplemental run title"
            defaultValue={nextTitle}
            onChange={(e) => {
              nextTitle = e.currentTarget.value;
            }}
            data-autofocus
          />
        </Stack>
      ),
      labels: { confirm: 'Create supplemental', cancel: 'Cancel' },
      onConfirm: () => {
        supplementMut.mutate(
          { title: nextTitle.trim() || `${run.title} — Supplement` },
          {
            onSuccess: ({ title_run }) => {
              notifications.show({
                title: 'Supplemental created',
                message: title_run.title,
                color: 'pine',
              });
              navigate(`/title-runs/${title_run.id}`);
            },
            onError: (e: Error) =>
              notifications.show({
                title: 'Could not create supplemental',
                message: e.message,
                color: 'red',
              }),
          },
        );
      },
    });
  }

  function confirmClose() {
    modals.openConfirmModal({
      title: 'Close this run?',
      children: (
        <Text size="sm">
          Closing freezes the runsheet. New instruments will need to go on a supplemental run.
          You can reopen later if needed.
        </Text>
      ),
      labels: { confirm: 'Close run', cancel: 'Cancel' },
      onConfirm: () => {
        closeMut.mutate(run.id, {
          onSuccess: () =>
            notifications.show({ title: 'Run closed', message: run.title, color: 'pine' }),
          onError: (e: Error) =>
            notifications.show({
              title: 'Could not close run',
              message: e.message,
              color: 'red',
            }),
        });
      },
    });
  }

  function confirmReopen() {
    modals.openConfirmModal({
      title: 'Reopen this run?',
      children: (
        <Text size="sm">
          Reopening allows new entries on this run again. The action is logged in the audit
          trail.
        </Text>
      ),
      labels: { confirm: 'Reopen run', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: () => {
        reopenMut.mutate(run.id, {
          onSuccess: () =>
            notifications.show({ title: 'Run reopened', message: run.title, color: 'pine' }),
          onError: (e: Error) =>
            notifications.show({
              title: 'Could not reopen run',
              message: e.message,
              color: 'red',
            }),
        });
      },
    });
  }

  // Derive drawer entry from current server data so edits reflect immediately.
  const drawerEntry = drawerEntryId ? (entries.find((e) => e.id === drawerEntryId) ?? null) : null;
  const selectedEntry = selectedEntryId ? entries.find((e) => e.id === selectedEntryId) : null;
  const commentsTarget = selectedEntry
    ? {
        type: 'runsheet_entry' as const,
        id: selectedEntry.id,
        label:
          selectedEntry.book && selectedEntry.page
            ? `${selectedEntry.book}/${selectedEntry.page}`
            : (selectedEntry.instrument_number ?? `Entry ${selectedEntry.id.slice(0, 8)}`),
      }
    : {
        type: 'title_run' as const,
        id: run.id,
        label: run.title,
      };

  return (
    <Stack gap="sm" px="sm" pb="sm" style={{ height: 'calc(100vh - 72px)' }}>
      <Breadcrumbs>
        <Anchor component={Link} to="/tracts">
          Tracts
        </Anchor>
        {tract && (
          <Anchor component={Link} to={`/tracts/${tract.id}`}>
            {tract.name}
          </Anchor>
        )}
        <Text>{run.title}</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-end">
        <Stack gap={0}>
          <Title order={2}>{run.title}</Title>
          <Group gap="xs">
            <Badge variant="light" color="pine">
              {run.status.replace('_', ' ')}
            </Badge>
            {!isOriginal && (
              <Badge variant="light" color="grape">
                Supplemental
              </Badge>
            )}
            {isClosed && (
              <Badge variant="filled" color="gray" leftSection={<IconLock size={12} />}>
                Closed
              </Badge>
            )}
            {run.through_date && (
              <Text size="sm" c="dimmed">
                Through {run.through_date}
              </Text>
            )}
            {run.sovereignty_date && (
              <Text size="sm" c="dimmed">
                · Sovereignty {run.sovereignty_date}
              </Text>
            )}
            <Text size="sm" c="dimmed">
              · {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
            </Text>
          </Group>
        </Stack>
        <Group gap="xs">
          {canEdit && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setDrawerEntryId(null);
                setDrawerOpen(true);
              }}
            >
              Add entry
            </Button>
          )}
          {(canWrite || canReopen) && (
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <Button variant="subtle" rightSection={<IconChevronDown size={14} />}>
                  More actions
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {canWrite && !isClosed && (
                  <Menu.Item leftSection={<IconLock size={14} />} onClick={confirmClose}>
                    Close run
                  </Menu.Item>
                )}
                {canReopen && isClosed && isOriginal && (
                  <Menu.Item leftSection={<IconLockOpen size={14} />} onClick={confirmReopen}>
                    Reopen run
                  </Menu.Item>
                )}
                {canWrite && isClosed && isOriginal && (
                  <Menu.Item
                    leftSection={<IconStackPush size={14} />}
                    onClick={openSupplementModal}
                  >
                    Add supplemental run
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>

      {isClosed && (
        <Alert
          color="gray"
          variant="light"
          icon={<IconLock size={16} />}
          title="This run is closed and read-only"
        >
          Closed{' '}
          {run.closed_at ? new Date(run.closed_at).toLocaleString() : ''}. New instruments belong
          on a supplemental run; use <strong>More actions → Add supplemental run</strong>.
        </Alert>
      )}

      {run.supplements && run.supplements.length > 0 && (
        <Alert color="grape" variant="light" title="Supplemental runs">
          <Group gap="xs">
            {run.supplements.map((s) => (
              <Anchor key={s.id} component={Link} to={`/title-runs/${s.id}`} size="sm">
                {s.title}
              </Anchor>
            ))}
          </Group>
        </Alert>
      )}

      {run.parent_title_run_id && (
        <Alert color="grape" variant="light" title="Supplement of">
          <Anchor component={Link} to={`/title-runs/${run.parent_title_run_id}`} size="sm">
            View original run
          </Anchor>
        </Alert>
      )}

      <Group
        align="stretch"
        gap="sm"
        wrap="nowrap"
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <Paper withBorder radius="md" style={{ flex: 1, overflow: 'auto' }}>
          <EntryGrid
            entries={entries}
            loading={entriesQ.isLoading}
            canWrite={canEdit}
            selectedId={selectedEntryId}
            onSelect={(e) => setSelectedEntryId(e.id)}
            onOpenDrawer={(e) => {
              setDrawerEntryId(e.id);
              setDrawerOpen(true);
            }}
          />
        </Paper>

        <Stack style={{ width: 360, minWidth: 360, minHeight: 0 }} gap="sm" visibleFrom="md">
          <CommentsPanel
            target={commentsTarget}
            onClearSelection={() => setSelectedEntryId(null)}
          />
        </Stack>
      </Group>

      <EntryFormDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerEntryId(null);
        }}
        titleRunId={run.id}
        entry={drawerEntry}
        onSaved={(e) => setDrawerEntryId(e.id)}
      />
    </Stack>
  );
}
